import React, { useState, useEffect } from "react";
import BalanceSheet from "../components/balancesheet";
import AdminBalanceDashboard from "../components/AdminBalanceDashboard";
import moment from "moment";
import UserList from "../components/reusable/usersList.jsx";
import { useLocation } from "react-router-dom";
import DateSelector from "../components/reusable/DateSelector.jsx";
import { useLoginContext } from "../Contexts/LoginContext.js";
import PageHeader from "../components/reusable/PageHeader.jsx";
import LoadingScreen from "../components/reusable/loadingScreen.jsx";
import { useTranslation } from "react-i18next";

const Balance = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const initialUser = location.state || {};
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [userId, setUserId] = useState(() =>
    initialUser.id ? String(initialUser.id) : "",
  );
  const [username, setUsername] = useState(initialUser.username || "");
  const [activeLedger, setActiveLedger] = useState(() =>
    initialUser.id ? "customer" : "admin",
  );
  const { role } = useLoginContext();
  const admin = role === "admin";
  const roleReady = typeof role === "string" && role.length > 0;

  useEffect(() => {
    if (location.state) {
      setUserId(location.state.id ? String(location.state.id) : "");
      setUsername(location.state.username || "");
      if (location.state.id) {
        setActiveLedger("customer");
      }
    }
  }, [location.state]);

  const handleDateRangeSelection = (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleUserSelect = (selectedUserId, selectedUser) => {
    setUserId(selectedUserId);
    if (selectedUser?.username) {
      setUsername(selectedUser.username);
    }
  };

  return (
    <div className="min-h-screen w-full bg-paper pb-20 font-sans text-primary">
      <main className="px-4 pt-4 max-w-lg mx-auto">
        <PageHeader title={t("balancePage.title")} />

        {admin && (
          <div
            className="mb-4 grid grid-cols-2 rounded-xl border border-line bg-white p-1 shadow-sm"
            aria-label={t("balancePage.view_label")}
          >
            <button
              type="button"
              onClick={() => setActiveLedger("admin")}
              aria-pressed={activeLedger === "admin"}
              className={`min-h-[42px] rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                activeLedger === "admin"
                  ? "bg-primary text-white shadow-sm"
                  : "text-[#6B7280] hover:bg-paper hover:text-primary"
              }`}
            >
              {t("balancePage.dairy_view")}
            </button>
            <button
              type="button"
              onClick={() => setActiveLedger("customer")}
              aria-pressed={activeLedger === "customer"}
              className={`min-h-[42px] rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                activeLedger === "customer"
                  ? "bg-primary text-white shadow-sm"
                  : "text-[#6B7280] hover:bg-paper hover:text-primary"
              }`}
            >
              {t("balancePage.customer_view")}
            </button>
          </div>
        )}

        {/* Filters are shared, but the account being viewed is explicit. */}
        <div className="bg-white border border-line rounded-2xl shadow-sm p-4 mb-5 space-y-4">
          {admin && activeLedger === "customer" && (
            <div className="w-full">
              <UserList
                label="Select User"
                onSelectUser={handleUserSelect}
                selectedUser={username}
                required
              />
            </div>
          )}

          <div className="w-full">
            <label className="block text-[9.5px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">
              {t("balancePage.date_label")}
            </label>
            <DateSelector handleChange={handleDateRangeSelection} />
            <p className="mt-2 text-[10.5px] font-medium text-[#6B7280]">
              {t("balancePage.sign_help")}
            </p>
          </div>
        </div>

        <div className="w-full">
          {!roleReady ? (
            <LoadingScreen />
          ) : admin && activeLedger === "admin" ? (
            <AdminBalanceDashboard startDate={startDate} endDate={endDate} />
          ) : admin && !userId ? (
            <div className="rounded-2xl border border-dashed border-line bg-white px-6 py-12 text-center shadow-sm">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h2 className="mt-4 text-[14px] font-bold text-primary">
                {t("balancePage.empty_title")}
              </h2>
              <p className="mx-auto mt-1.5 max-w-[260px] text-[12px] leading-relaxed text-[#6B7280]">
                {t("balancePage.empty_body")}
              </p>
            </div>
          ) : (
            <BalanceSheet
              startDate={startDate}
              endDate={endDate}
              userId={admin ? userId : ""}
              role={role}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default Balance;
